import React, { useState, useEffect } from "react"
import Button from "@material-ui/core/Button"
import CssBaseline from "@material-ui/core/CssBaseline"
import TextField from "@material-ui/core/TextField"
import Grid from "@material-ui/core/Grid"
import Typography from "@material-ui/core/Typography"
import { Box } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import Container from "@material-ui/core/Container"
import IconButton from "@material-ui/core/IconButton"
import Visibility from "@material-ui/icons/Visibility"
import VisibilityOff from "@material-ui/icons/VisibilityOff"
import RequestSnackbar from "@/components/common/RequestSnackbar"
import Loading from "@/components/common/Loading"
import { userUpdateRequest } from "@/redux/actions/userAction"
import { useSelector, useDispatch } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import { generatePassword, emailFormatCheck, urlPath } from "@/utils"
import clsx from "clsx"
import globalUseStyles from "@/globalUseStyles"

const useStyles = makeStyles((theme) => ({
    contentHeader: {
        borderBottomWidth: 2,
        borderBottomStyle: "solid",
        borderColor: theme.palette.sparcaLightGrey.main,
    },
    contentHeaderElement: {
        margin: theme.spacing(4),
    },
    contentHeaderButton: {
        [theme.breakpoints.up("md")]: {
            display: "none",
        },
    },
    ProjectDashboardContentBodyWrapper: {
        position: "relative",
        width: "100%",
        height: "100%",
    },
    paper: {
        marginTop: theme.spacing(8),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {
        width: "100%", // Fix IE 11 issue.
        marginTop: theme.spacing(1),
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
    centerText: {
        textAlign: "center",
    },
    inputField: {
        marginTop: "10px",
        marginBottom: "10px",
    },
    displayInlineBlock: {
        display: "inline-block",
        verticalAlign: "middle",
    },
}))


export default function Profile() {
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [email, setEmail] = useState("")
    const [emailChanged, setEmailChanged] = useState(false)
    const [passwordNew, setPasswordNew] = useState("")
    const [passwordNewInputOpen, setPasswordNewInputOpen] = useState(false)
    const [errorMsg, setErrorMsg] = useState({ firstName: "", lastName: "", email: "", password: "" })
    const [formUpdated, setFormUpdated] = useState(false)
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [requestSuccessStatus, setRequestSuccessStatus] = useState(false)
    const [responseMessage, setResponseMessage] = useState("")
    const [showPassword, setShowPassword] = useState(true)

    const navigate = useNavigate()

    const dispatch = useDispatch()
    const userData = useSelector((state) => state.userReducer)
    const { loading } = userData
    const classes = useStyles()

    useEffect(() => {
        let timer
        const detectUserChange = () => {
            if (userData && userData.passwordChange) {
                setRequestSuccessStatus(true)
                setResponseMessage("You have reset your password, please login again.")
                timer = setTimeout(() => {
                    navigate(urlPath("login"))
                }, 1000)
            } else {
                if (userData && userData.user.firstName) setFirstName(userData.user.firstName)
                if (userData && userData.user.lastName) setLastName(userData.user.lastName)
                if (userData && userData.user.email) setEmail(userData.user.email)
                if (userData.successStatus) {
                    setRequestSuccessStatus(true)
                    setResponseMessage(userData.message)
                } else {
                    setRequestSuccessStatus(false)
                    setResponseMessage(userData.message)
                }
            }
        }
        detectUserChange()
        return () => clearTimeout(timer)
    }, [userData])

    const handleSubmit = (e) => {
        e.preventDefault()
        if (formUpdated) {
            if (!firstName)
                setErrorMsg((prevErrorMsg) => ({
                    ...prevErrorMsg,
                    firstName: "Please input first name",
                }))
            if (!lastName)
                setErrorMsg((prevErrorMsg) => ({
                    ...prevErrorMsg,
                    lastName: "Please input last name",
                }))
            if (!email)
                setErrorMsg((prevErrorMsg) => ({
                    ...prevErrorMsg,
                    email: "Please input email",
                }))
            else if (!emailFormatCheck(email)) {
                setErrorMsg((prevErrorMsg) => ({
                    ...prevErrorMsg,
                    email: "Email format is not correct",
                }))
            }
            if (passwordNewInputOpen && !passwordNew) {
                setErrorMsg((prevErrorMsg) => ({
                    ...prevErrorMsg,
                    password: "Please input password",
                }))
            }
            let newUser
            if (errorMsg.firstName === "" && errorMsg.lastName === "" && errorMsg.email === "") {
                newUser = {
                    userId: userData.user.userId,
                    firstName,
                    lastName,
                }
                if (emailChanged) newUser.email = email
                if (passwordNew) newUser.password = passwordNew
                if (passwordNew) {
                    if (errorMsg.password === "") {
                        dispatch(userUpdateRequest(newUser))
                    }
                } else {
                    dispatch(userUpdateRequest(newUser))
                }
                setSnackbarOpen(true)
            }
        } else {
            navigate.goBack()
        }
    }

    const handleClickShowPassword = () => {
        setShowPassword((prev) => !prev)
    }

    const handleMouseDownPassword = (event) => {
        event.preventDefault()
    }

    const handleSnackbarOpenStatus = (status) => {
        setSnackbarOpen(status)
    }

    const handlePasswordButton = () => {
        setPasswordNewInputOpen(true)
        const passwordNewGenerated = generatePassword()
        setPasswordNew(passwordNewGenerated)
        setFormUpdated(true)
    }

    const globalClasses = globalUseStyles()

    return (
        <>
            <Box component="div" className={classes.contentHeader}>
                <Box component="div" className={classes.contentHeaderElement}>
                    <Button onClick={() => navigate('/dashboar')} color="primary" variant="contained" className={`${classes.backButton}`}>
                        Home Page
                    </Button>
                </Box>
            </Box>
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <form className={classes.root} noValidate autoComplete="off">
                    <div className={classes.paper}>
                        <Typography component="h1" variant="h4">
                            Edit Profile
                        </Typography>
                        <Grid container spacing={1} alignItems="flex-end">
                            <Grid item xs={12}>
                                <TextField
                                    variant="outlined"
                                    required
                                    id="first-name"
                                    label="First Name"
                                    name="firstName"
                                    fullWidth
                                    defaultValue={userData.user && userData.user.firstName}
                                    margin="normal"
                                    error={!!errorMsg.firstName}
                                    helperText={errorMsg.firstName}
                                    onChange={(e) => {
                                        setFirstName(e.target.value)
                                        setErrorMsg((prevErrorMsg) => ({
                                            ...prevErrorMsg,
                                            firstName: "",
                                        }))
                                        if (!formUpdated) {
                                            setFormUpdated(true)
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    variant="outlined"
                                    required
                                    id="last-name"
                                    label="Last Name"
                                    name="lastName"
                                    fullWidth
                                    defaultValue={userData.user && userData.user.lastName}
                                    margin="normal"
                                    error={!!errorMsg.lastName}
                                    helperText={errorMsg.lastName}
                                    onChange={(e) => {
                                        setLastName(e.target.value)
                                        setErrorMsg((prevErrorMsg) => ({
                                            ...prevErrorMsg,
                                            lastName: "",
                                        }))
                                        if (!formUpdated) {
                                            setFormUpdated(true)
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    variant="outlined"
                                    required
                                    id="email"
                                    label="Email"
                                    name="email"
                                    fullWidth
                                    defaultValue={userData.user && userData.user.email}
                                    margin="normal"
                                    error={!!errorMsg.email}
                                    helperText={errorMsg.email}
                                    onChange={(e) => {
                                        setEmail(e.target.value)
                                        setEmailChanged(true)
                                        setErrorMsg((prevErrorMsg) => ({
                                            ...prevErrorMsg,
                                            email: "",
                                        }))
                                        if (!formUpdated) {
                                            setFormUpdated(true)
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container justify="space-between" alignItems="center" style={{ marginTop: "10px" }}>
                                    <Typography variant="body1" component="p">
                                        New Password:
                                    </Typography>
                                    <div>
                                        {!passwordNewInputOpen ? (
                                            <Button variant="outlined" color="primary" onClick={handlePasswordButton}>
                                                Generate new password
                                            </Button>
                                        ) : (
                                            <>
                                                <TextField
                                                    className={classes.displayInlineBlock}
                                                    variant="outlined"
                                                    required
                                                    size="small"
                                                    id="new-password"
                                                    name="newPassword"
                                                    defaultValue={passwordNew}
                                                    error={!!errorMsg.password}
                                                    helperText={errorMsg.password}
                                                    type={showPassword ? "text" : "password"}
                                                    onChange={(e) => {
                                                        setPasswordNew(e.target.value)
                                                        setErrorMsg((prevErrorMsg) => ({
                                                            ...prevErrorMsg,
                                                            password: "",
                                                        }))
                                                        if (!formUpdated) {
                                                            setFormUpdated(true)
                                                        }
                                                    }}
                                                />
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                    className={classes.displayInlineBlock}
                                                >
                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </>
                                        )}
                                    </div>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            className={classes.submit}
                            onClick={handleSubmit}
                        >
                            {!formUpdated ? "Cancel" : "Update profile"}
                        </Button>
                    </div>
                </form>
            </Container>
            {loading ? (
                <Loading />
            ) : (
                <RequestSnackbar
                    status={requestSuccessStatus}
                    message={responseMessage}
                    snackbarOpen={snackbarOpen}
                    handleSnackbarOpenStatus={handleSnackbarOpenStatus}
                />
            )}
        </>
    )
}
